import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { Html5Qrcode } from 'html5-qrcode';
import {
  AttendanceAction,
  AttendanceScanResult,
  AttendanceService,
  AttendanceStatus,
  AttendanceStudent,
} from 'src/app/services/attendance.service';
import { LanguageService } from 'src/app/services/language.service';
import { computeQrBoxSize, measureScanHole } from 'src/app/utils/scanner-viewfinder';

type FeedbackKind = 'success' | 'already' | 'error';

interface ScanFeedback {
  kind: FeedbackKind;
  titleKey: string;
  studentName: string;
  className: string;
  action: AttendanceAction;
  status?: AttendanceStatus | null;
  scanTime?: string;
  message?: string;
}

@Component({
  selector: 'app-attendance-scanner',
  templateUrl: './attendance-scanner.component.html',
  styleUrls: ['./attendance-scanner.component.css']
})
export class AttendanceScannerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scannerStage') scannerStage?: ElementRef<HTMLElement>;

  private scanner: Html5Qrcode | null = null;
  private scanning = false;
  private starting = false;
  private scanningBusy = false;
  private lastHandledCode = '';
  private lastHandledAt = 0;
  private audioCtx: AudioContext | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private onWindowResize = () => this.syncViewfinder();
  readonly scannerElementId = 'attendance-qr-reader';

  scanMode: AttendanceAction = 'check_in';
  cameraActive = false;
  cameraError = '';
  feedback: ScanFeedback | null = null;
  viewfinderStyle: Record<string, string> = {
    top: '15%',
    left: '15%',
    width: '70%',
    height: '70%',
  };

  constructor(
    private attendanceService: AttendanceService,
    private languageService: LanguageService,
    private location: Location,
  ) {}

  async ngAfterViewInit() {
    this.bindStageObserver();
    await this.startCamera();
  }

  async ngOnDestroy() {
    this.unbindStageObserver();
    await this.stopCamera();
    try {
      await this.audioCtx?.close();
    } catch {
      // ignore
    }
    this.audioCtx = null;
  }

  goBack(): void {
    this.location.back();
  }

  setMode(mode: AttendanceAction): void {
    this.unlockAudio();
    if (this.scanMode === mode) {
      return;
    }
    this.scanMode = mode;
    this.feedback = null;
    this.lastHandledCode = '';
  }

  async onStartCameraClick(): Promise<void> {
    this.unlockAudio();
    await this.startCamera();
  }

  studentName(student?: AttendanceStudent | null): string {
    if (!student) {
      return '—';
    }
    const isAr = this.languageService.current === 'ar';
    const name = isAr
      ? `${student.firstNameAr || ''} ${student.lastNameAr || ''}`.trim()
      : `${student.firstNameEn || ''} ${student.lastNameEn || ''}`.trim();
    return name
      || `${student.firstNameAr || ''} ${student.lastNameAr || ''}`.trim()
      || `${student.firstNameEn || ''} ${student.lastNameEn || ''}`.trim()
      || '—';
  }

  statusKey(status?: AttendanceStatus | null): string {
    return `ATTENDANCE.STATUS_${(status || 'on_time').toUpperCase()}`;
  }

  private isInsecureRemoteOrigin(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    if (window.isSecureContext) {
      return false;
    }
    const host = location.hostname;
    return host !== 'localhost' && host !== '127.0.0.1';
  }

  private async startCamera() {
    if (this.starting || this.cameraActive) {
      return;
    }

    this.starting = true;
    this.cameraError = '';

    if (this.isInsecureRemoteOrigin()) {
      this.cameraError = 'ATTENDANCE.HTTPS_REQUIRED';
      this.starting = false;
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.cameraError = 'ATTENDANCE.CAMERA_UNSUPPORTED';
      this.starting = false;
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 150));

    const scanConfig = {
      fps: 8,
      qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
        const size = computeQrBoxSize(viewfinderWidth, viewfinderHeight);
        return { width: size, height: size };
      },
      aspectRatio: 1,
      experimentalFeatures: {
        useBarCodeDetectorIfSupported: true,
      },
    };

    const cameraConfigs: Array<string | MediaTrackConstraints> = [
      { facingMode: { ideal: 'environment' } },
      { facingMode: 'environment' },
      { facingMode: { ideal: 'user' } },
      { facingMode: 'user' },
    ];

    for (const cameraConfig of cameraConfigs) {
      try {
        await this.tryStart(cameraConfig, scanConfig);
        this.cameraActive = true;
        this.scanning = true;
        this.starting = false;
        this.queueViewfinderSync();
        return;
      } catch {
        await this.safeResetScanner();
      }
    }

    try {
      const cameras = await Html5Qrcode.getCameras();
      await new Promise(resolve => setTimeout(resolve, 350));
      const preferred = cameras.find(camera =>
        /back|rear|environment|خلف|خلفي|wide/i.test(camera.label || '')
      ) || cameras[0];
      if (preferred?.id) {
        await this.tryStart(preferred.id, scanConfig);
        this.cameraActive = true;
        this.scanning = true;
        this.starting = false;
        this.queueViewfinderSync();
        return;
      }
    } catch {
      await this.safeResetScanner();
    }

    this.cameraError = 'ATTENDANCE.CAMERA_FAILED';
    this.cameraActive = false;
    this.scanning = false;
    this.starting = false;
  }

  private async tryStart(
    cameraConfig: string | MediaTrackConstraints,
    scanConfig: {
      fps: number;
      qrbox: (viewfinderWidth: number, viewfinderHeight: number) => { width: number; height: number };
      aspectRatio: number;
      experimentalFeatures: { useBarCodeDetectorIfSupported: boolean };
    },
  ): Promise<void> {
    if (!this.scanner) {
      this.scanner = new Html5Qrcode(this.scannerElementId, { verbose: false });
    }
    await this.scanner.start(
      cameraConfig,
      scanConfig,
      (decoded) => { void this.onCodeDetected(decoded); },
      () => undefined,
    );
  }

  private async safeResetScanner(): Promise<void> {
    if (!this.scanner) {
      return;
    }
    try {
      const state = this.scanner.getState?.();
      if (state === 2 || state === 3 || this.scanning) {
        await this.scanner.stop();
      }
    } catch {
      // ignore
    }
    try {
      await this.scanner.clear();
    } catch {
      // ignore
    }
    this.scanner = null;
    this.scanning = false;
    this.cameraActive = false;
  }

  private async stopCamera() {
    await this.safeResetScanner();
  }

  private bindStageObserver(): void {
    window.addEventListener('resize', this.onWindowResize, { passive: true });
    window.addEventListener('orientationchange', this.onWindowResize, { passive: true });
    const stage = this.scannerStage?.nativeElement;
    if (stage && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.syncViewfinder());
      this.resizeObserver.observe(stage);
    }
  }

  private unbindStageObserver(): void {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('orientationchange', this.onWindowResize);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private queueViewfinderSync(): void {
    // iPhone lays out video + shaded region after start.
    const delays = [0, 50, 150, 350, 700, 1200];
    for (const ms of delays) {
      setTimeout(() => this.syncViewfinder(), ms);
    }
  }

  private syncViewfinder(): void {
    if (!this.cameraActive) {
      return;
    }
    const stage = this.scannerStage?.nativeElement;
    if (!stage) {
      return;
    }
    const hole = measureScanHole(stage);
    if (!hole) {
      return;
    }
    this.viewfinderStyle = {
      top: `${Math.round(hole.top)}px`,
      left: `${Math.round(hole.left)}px`,
      width: `${Math.round(hole.width)}px`,
      height: `${Math.round(hole.height)}px`,
    };
  }

  private async onCodeDetected(code: string) {
    const trimmed = String(code || '').trim();
    if (!trimmed || this.scanningBusy) {
      return;
    }
    const now = Date.now();
    if (trimmed === this.lastHandledCode && now - this.lastHandledAt < 2500) {
      return;
    }
    this.lastHandledCode = trimmed;
    this.lastHandledAt = now;
    this.scanningBusy = true;
    try {
      const result = await this.attendanceService.scan(trimmed, this.scanMode);
      this.applyScanResult(result);
    } catch (error: any) {
      this.feedback = {
        kind: 'error',
        titleKey: 'ATTENDANCE.SCAN_FAILED',
        studentName: '',
        className: '',
        action: this.scanMode,
        message: error?.error?.errorMessages?.[0]?.message || '',
      };
      this.beep('error');
    } finally {
      this.scanningBusy = false;
    }
  }

  private applyScanResult(result: AttendanceScanResult): void {
    const status = result.action === 'check_out'
      ? result.attendance?.checkOut?.status
      : result.attendance?.checkIn?.status;

    if (result.alreadyScanned) {
      this.feedback = {
        kind: 'already',
        titleKey: result.reason === 'already_checked_out'
          ? 'ATTENDANCE.ALREADY_CHECKED_OUT'
          : 'ATTENDANCE.ALREADY_CHECKED_IN',
        studentName: this.studentName(result.student),
        className: result.student?.class?.name || '',
        action: result.action,
        status: status || null,
        scanTime: result.scanTime,
      };
      this.beep('already');
      return;
    }

    this.feedback = {
      kind: 'success',
      titleKey: result.action === 'check_out'
        ? 'ATTENDANCE.SCAN_CHECK_OUT_OK'
        : 'ATTENDANCE.SCAN_CHECK_IN_OK',
      studentName: this.studentName(result.student),
      className: result.student?.class?.name || '',
      action: result.action,
      status: status || null,
      scanTime: result.scanTime,
    };
    this.beep('success');
  }

  private unlockAudio(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) {
      return;
    }
    if (!this.audioCtx) {
      this.audioCtx = new Ctx();
    }
    if (this.audioCtx.state === 'suspended') {
      void this.audioCtx.resume();
    }
  }

  private beep(kind: FeedbackKind): void {
    try {
      this.unlockAudio();
      if (!this.audioCtx) {
        return;
      }
      const ctx = this.audioCtx;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = kind === 'success' ? 880 : kind === 'already' ? 520 : 220;
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + (kind === 'error' ? 0.35 : 0.18));
      osc.start(now);
      osc.stop(now + 0.4);
    } catch {
      // ignore audio failures
    }
  }
}
